import EqualizerIcon from '@mui/icons-material/Equalizer';
import PermDeviceInformationIcon from '@mui/icons-material/PermDeviceInformation';
import { Box, Divider, FormControlLabel, IconButton, Stack, Switch, Tooltip } from '@mui/material';
import { useUpdateDeviceGroup } from 'gen/client';
import { DevicespecsDTO, UserDeviceGroupsLastSeenDTO, UserUIDeviceGroupResponseDTO } from 'gen/model';
import { useState } from 'react';

import { useHasNext } from '@ui/Hooks/useHasNext';

import DeviceImage from 'kerberus-components/src/Components/Avatars/DeviceImage';
import { dayjs } from 'kerberus-components/src/Components/Dayjs/dayjs-utils';
import { SectionHeader } from 'kerberus-components/src/Components/Layout/SectionHeader';
import ChartsModal from 'kerberus-components/src/Components/Modals/ChartModal';
import getActivityColor from 'kerberus-components/src/Utils/getActivityColor';

interface DeviceContentProps {
    deviceGroup: UserUIDeviceGroupResponseDTO;
    deviceSpec: DevicespecsDTO | undefined;
    lastSeen: UserDeviceGroupsLastSeenDTO | null;
    lastSeenMinutes: number | null;
    openInfo?: () => void;
}

export default function DeviceContent({
    deviceGroup,
    deviceSpec,
    lastSeen,
    lastSeenMinutes,
    openInfo,
}: Readonly<DeviceContentProps>) {
    const activity = lastSeenMinutes ? (lastSeenMinutes <= 10 ? 'Active' : 'Inactive') : 'Inactive';
    const siteLocation = lastSeen?.instance_role ?? 'Unknown';
    const lastSeenTime = lastSeen ? dayjs(lastSeen.timestamp).fromNow() : 'Unknown';

    const hasNext = useHasNext();

    const [chartModal, setChartModal] = useState(false);
    const handleChartModalOpen = () => {
        setChartModal(true);
    };
    const handleChartModalClose = () => {
        setChartModal(false);
    };

    const { trigger } = useUpdateDeviceGroup(deviceGroup.id);

    const handleDeviceVisibility = async () => {
        try {
            if (!deviceGroup.id) {
                return;
            }
            await trigger(
                { visible: !deviceGroup.visible },
                {
                    optimisticData(currentData) {
                        return {
                            ...currentData,
                            visible: !deviceGroup.visible,
                        };
                    },
                    revalidate: false,
                },
            );
        } catch (error) {
            alert(error);
        }
    };

    const handleDeviceLocation = async () => {
        try {
            if (!deviceGroup.id) {
                return;
            }
            await trigger(
                { location: !deviceGroup.location },
                {
                    optimisticData(currentData) {
                        return {
                            ...currentData,
                            location: !deviceGroup.location,
                        };
                    },
                    revalidate: false,
                },
            );
        } catch (error) {
            alert(error);
        }
    };

    const isDesktop = !deviceGroup.devicespec_id;

    return (
        <Stack
            spacing={1}
            sx={{
                backgroundColor: '#edf2f6',
                p: 2,
            }}
        >
            <Stack
                direction={{ md: 'row', xs: 'column' }}
                divider={
                    <>
                        <Divider
                            flexItem
                            orientation="vertical"
                            sx={{ isplay: { md: 'initial', xs: 'none' }, pr: 2 }}
                        />
                        <Divider flexItem orientation="horizontal" sx={{ display: { md: 'none', xs: 'initial' } }} />
                    </>
                }
                spacing={{ md: 2, xs: 1 }}
            >
                <Stack>
                    <SectionHeader title="Image" />
                    <Box
                        sx={{
                            height: 175,
                            py: 2,
                            width: 175,
                        }}
                    >
                        <DeviceImage desktop={isDesktop} imageName={deviceSpec?.image} size={150} />
                    </Box>
                </Stack>
                <Stack
                    direction={{ sm: 'row', xs: 'column' }}
                    divider={
                        <>
                            <Divider
                                flexItem
                                orientation="vertical"
                                sx={{ isplay: { md: 'initial', xs: 'none' }, pr: 2 }}
                            />
                            <Divider
                                flexItem
                                orientation="horizontal"
                                sx={{ display: { md: 'none', xs: 'initial' } }}
                            />
                        </>
                    }
                    spacing={{ md: 2, xs: 1 }}
                    sx={{ backgroundColor: { md: '', xs: '' } }}
                >
                    {isDesktop ? (
                        <Stack
                            sx={{
                                flex: 1,
                                minWidth: 250,
                                position: 'relative',
                            }}
                        >
                            {hasNext ? (
                                <Box sx={{ position: 'absolute', right: { md: -20, xs: 0 }, top: -2 }}>
                                    <Tooltip title="Activity Chart">
                                        <IconButton onClick={handleChartModalOpen}>
                                            <EqualizerIcon sx={{ color: 'mlBlue.main', height: 30, width: 30 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : null}
                            {deviceGroup.custom_device_spec?.type === 'mobile' ? (
                                <>
                                    <SectionHeader
                                        description={deviceGroup.custom_device_spec.device as string}
                                        title="Device"
                                    />
                                    <SectionHeader
                                        description={deviceGroup.custom_device_spec.manufacturer as string}
                                        title="Manufacturer"
                                    />
                                    <SectionHeader
                                        description={deviceGroup.custom_device_spec.model as string}
                                        title="Model"
                                    />
                                </>
                            ) : (
                                <>
                                    {deviceGroup.custom_device_spec?.name ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.name as string}
                                            title="Name"
                                        />
                                    ) : null}
                                    {deviceGroup.custom_device_spec?.model ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.model as string}
                                            title="Model"
                                        />
                                    ) : null}
                                    {deviceGroup.custom_device_spec?.os ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.os as string}
                                            title="Operating System"
                                        />
                                    ) : null}
                                    {deviceGroup.custom_device_spec?.cpu ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.cpu as string}
                                            title="CPU"
                                        />
                                    ) : null}
                                    {deviceGroup.custom_device_spec?.product ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.product as string}
                                            title="Product"
                                        />
                                    ) : null}
                                    {deviceGroup.custom_device_spec?.ram ? (
                                        <SectionHeader
                                            description={deviceGroup.custom_device_spec.ram as string}
                                            title="RAM"
                                        />
                                    ) : null}
                                </>
                            )}
                            <ChartsModal
                                availableCharts={['Bar', 'Line', 'Pie']}
                                onClose={handleChartModalClose}
                                open={chartModal}
                            />
                        </Stack>
                    ) : (
                        <Stack
                            sx={{
                                flex: 1,
                                minWidth: 200,
                                position: 'relative',
                            }}
                        >
                            <Box sx={{ position: 'absolute', right: { md: -20, xs: 0 }, top: -2 }}>
                                {hasNext ? (
                                    <Tooltip title="Activity Chart">
                                        <IconButton onClick={handleChartModalOpen}>
                                            <EqualizerIcon sx={{ color: 'mlBlue.main', height: 30, width: 30 }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <IconButton onClick={openInfo}>
                                    <PermDeviceInformationIcon sx={{ color: 'primary.main' }} />
                                </IconButton>
                            </Box>
                            <SectionHeader description={deviceSpec?.brand} title="Brand" />
                            <SectionHeader description={deviceSpec?.model} title="Model" />
                            <SectionHeader description={deviceSpec?.operatingSystem} title="Operating System" />
                            <SectionHeader description={deviceSpec?.resolution} title="Screen Resolution" />
                            <ChartsModal
                                availableCharts={['Bar', 'Line', 'Pie']}
                                onClose={handleChartModalClose}
                                open={chartModal}
                            />
                        </Stack>
                    )}
                    <Stack
                        sx={{
                            flex: 1,
                            minWidth: 100,
                        }}
                    >
                        <SectionHeader
                            description={
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: getActivityColor({ busy: false, lastSeenMinutes }),
                                            borderRadius: '50%',
                                            height: 10,
                                            width: 10,
                                        }}
                                    />
                                    <span>{activity}</span>
                                </Stack>
                            }
                            title="Activity"
                        />
                        <SectionHeader description={siteLocation} title="Location" />
                        <SectionHeader description={lastSeenTime} title="Last Seen" />
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        pr: 4,
                    }}
                >
                    <Stack>
                        <SectionHeader description="This device can be tracked within system" title="Visibility" />
                        <FormControlLabel
                            control={<Switch checked={deviceGroup.visible} onChange={handleDeviceVisibility} />}
                            label={<strong>Enable</strong>}
                        />
                    </Stack>
                    <Stack>
                        <SectionHeader description="Display device location within system" title="Location" />
                        <FormControlLabel
                            control={<Switch checked={deviceGroup.location} onChange={handleDeviceLocation} />}
                            label={<strong>Enable</strong>}
                        />
                    </Stack>
                </Stack>
            </Stack>
            {deviceGroup.description ? (
                <>
                    <Divider />
                    <Stack>
                        <SectionHeader description={deviceGroup.description} title="Description" />
                    </Stack>
                </>
            ) : null}
            {deviceGroup.installation_id ? (
                <>
                    <Divider />
                    <Stack>
                        <SectionHeader description={deviceGroup.installation_id} title="Installation ID" />
                    </Stack>
                </>
            ) : null}
        </Stack>
    );
}
